// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Search, Calendar, LayoutDashboard, Menu, LogOut, Settings, User, ArrowUpRight, Tent } from 'lucide-react';
import CartButton from '../../../../components/CartDrawer/CartButton';

import { LinkedLogo, NamedLink } from '../../../../components';
import { Button } from '../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '../../../../components/ui/avatar';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../../components/ui/sheet';

import '../../../../styles/tailwind.css';

interface TopbarDesktopStyledProps {
  isAuthenticated: boolean;
  currentUser: any;
  currentPage: string;
  onLogout: (history: any) => void;
  notificationCount: number;
  config: any;
  routeConfiguration: any;
  intl: any;
  history: any;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const TopbarDesktopStyled: React.FC<TopbarDesktopStyledProps> = ({
  isAuthenticated,
  currentUser,
  currentPage,
  onLogout,
  notificationCount,
  config,
  routeConfiguration,
  intl,
  history,
  mobileMenuOpen,
  setMobileMenuOpen,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 8);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const displayName = currentUser?.attributes?.profile?.displayName || '';
  const abbreviatedName = currentUser?.attributes?.profile?.abbreviatedName || '';
  const email = currentUser?.attributes?.email || '';
  const profileImageUrl = currentUser?.profileImage?.attributes?.variants?.['square-small2x']?.url
    || currentUser?.profileImage?.attributes?.variants?.['square-small']?.url
    || null;

  const handleLogout = () => {
    onLogout(history);
  };

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileLogout = () => {
    setMobileMenuOpen(false);
    onLogout(history);
  };

  const navLinks = [
    {
      name: 'SearchPage',
      label: intl.formatMessage({ id: 'TopbarStyled.search' }),
      icon: Search,
    },
    {
      name: 'SchedulePage',
      label: intl.formatMessage({ id: 'TopbarStyled.schedule' }),
      icon: Calendar,
    },
    {
      name: 'DashboardBasePage',
      label: intl.formatMessage({ id: 'TopbarStyled.dashboard' }),
      icon: LayoutDashboard,
    },
    {
      name: 'CampScoutPage',
      label: 'CampScout',
      icon: Tent,
    },
  ];

  const isActive = (pageName: string) => currentPage === pageName;

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
      scrolled
        ? "bg-background/95 shadow-md backdrop-blur supports-[backdrop-filter]:bg-background/80"
        : "bg-background/80 backdrop-blur"
    }`}>
      <div className="container flex h-18 items-center justify-between gap-4 px-4 md:px-6 py-4">
        {/* Left: Logo + Desktop Nav */}
        <div className="flex items-center gap-8">
          <LinkedLogo
            id="logo-topbar-styled-desktop"
            layout="desktop"
            alt={intl.formatMessage(
              { id: 'TopbarDesktop.logo' },
              { marketplaceName: config?.marketplaceName }
            )}
            linkToExternalSite={config?.topbar?.logoLink}
          />
          <nav className="hidden lg:flex items-center gap-1" aria-label={intl.formatMessage({ id: 'TopbarStyled.navigation' })}>
            {navLinks.map(link => {
              const Icon = link.icon;
              const active = isActive(link.name);
              return (
                <NamedLink
                  key={link.name}
                  name={link.name}
                  className="no-underline hover:no-underline"
                >
                  <Button variant={active ? "secondary" : "ghost"} size="sm" className="gap-2 font-medium">
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Button>
                </NamedLink>
              );
            })}
          </nav>
        </div>

        {/* Right: Auth Section + Mobile Menu */}
        <div className="flex items-center gap-3">
          {/* Notification badge */}
          {authenticatedOnClientSide && notificationCount > 0 && (
            <NamedLink
              name="InboxPage"
              params={{ tab: 'orders' }}
              className="relative inline-flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors no-underline hover:no-underline"
            >
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
              <ArrowUpRight className="h-4 w-4" />
            </NamedLink>
          )}

          {/* Guest: Sign In + Sign Up */}
          {!isAuthenticatedOrJustHydrated && (
            <>
              <NamedLink
                name="LoginPage"
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors duration-300 hidden sm:inline-block no-underline hover:no-underline"
              >
                {intl.formatMessage({ id: 'TopbarStyled.signIn' })}
              </NamedLink>
              <NamedLink
                name="SignupPage"
                className="no-underline hover:no-underline"
              >
                <span className="servibe-btn servibe-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm">
                  <span>{intl.formatMessage({ id: 'TopbarStyled.signUp' })}</span>
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </NamedLink>
            </>
          )}

          {/* Cart */}
          {authenticatedOnClientSide && <CartButton />}

          {/* Authenticated: Avatar Dropdown */}
          {authenticatedOnClientSide && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Avatar className="h-9 w-9">
                    {profileImageUrl ? (
                      <AvatarImage src={profileImageUrl} alt={displayName} />
                    ) : null}
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                      {abbreviatedName || displayName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{displayName}</p>
                    {email && (
                      <p className="text-xs leading-none text-muted-foreground">{email}</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <NamedLink
                    name="DashboardBasePage"
                    className="flex items-center cursor-pointer no-underline hover:no-underline text-inherit"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {intl.formatMessage({ id: 'TopbarStyled.menuDashboard' })}
                  </NamedLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NamedLink
                    name="ProfileSettingsPage"
                    className="flex items-center cursor-pointer no-underline hover:no-underline text-inherit"
                  >
                    <User className="mr-2 h-4 w-4" />
                    {intl.formatMessage({ id: 'TopbarStyled.menuProfile' })}
                  </NamedLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NamedLink
                    name="PasswordChangePage"
                    className="flex items-center cursor-pointer no-underline hover:no-underline text-inherit"
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    {intl.formatMessage({ id: 'TopbarStyled.menuSettings' })}
                  </NamedLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="flex items-center cursor-pointer text-destructive focus:text-destructive"
                  onSelect={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {intl.formatMessage({ id: 'TopbarStyled.menuLogout' })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile Menu Sheet */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label={intl.formatMessage({ id: 'TopbarStyled.openMenu' })}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[360px] flex flex-col">
              <SheetHeader className="text-left pb-4 border-b border-border">
                <SheetTitle>{intl.formatMessage({ id: 'TopbarStyled.menuTitle' })}</SheetTitle>
              </SheetHeader>

              {/* User info section (authenticated) */}
              {isAuthenticated && currentUser && (
                <div className="flex items-center gap-3 py-4 border-b border-border">
                  <Avatar className="h-10 w-10">
                    {profileImageUrl ? (
                      <AvatarImage src={profileImageUrl} alt={displayName} />
                    ) : null}
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                      {abbreviatedName || displayName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-medium truncate">{displayName}</span>
                    {email && (
                      <span className="text-xs text-muted-foreground truncate">{email}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation links */}
              <nav className="flex flex-col gap-1 py-4 flex-1">
                {navLinks.map(link => {
                  const Icon = link.icon;
                  const active = isActive(link.name);
                  return (
                    <NamedLink
                      key={link.name}
                      name={link.name}
                      className="no-underline hover:no-underline"
                      onClick={handleMobileLinkClick}
                    >
                      <Button variant={active ? "secondary" : "ghost"} className="w-full justify-start gap-3">
                        <Icon className="h-4 w-4" />
                        {link.label}
                      </Button>
                    </NamedLink>
                  );
                })}

                {/* Authenticated-only links */}
                {isAuthenticated && (
                  <>
                    <div className="my-2 h-px bg-border" />
                    <NamedLink
                      name="ProfileSettingsPage"
                      className="no-underline hover:no-underline"
                      onClick={handleMobileLinkClick}
                    >
                      <Button variant={isActive('ProfileSettingsPage') ? "secondary" : "ghost"} className="w-full justify-start gap-3">
                        <User className="h-4 w-4" />
                        {intl.formatMessage({ id: 'TopbarStyled.menuProfile' })}
                      </Button>
                    </NamedLink>
                    <NamedLink
                      name="PasswordChangePage"
                      className="no-underline hover:no-underline"
                      onClick={handleMobileLinkClick}
                    >
                      <Button variant={isActive('PasswordChangePage') ? "secondary" : "ghost"} className="w-full justify-start gap-3">
                        <Settings className="h-4 w-4" />
                        {intl.formatMessage({ id: 'TopbarStyled.menuSettings' })}
                      </Button>
                    </NamedLink>
                  </>
                )}
              </nav>

              {/* Footer: auth actions */}
              <div className="mt-auto border-t border-border pt-4">
                {isAuthenticated ? (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleMobileLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    {intl.formatMessage({ id: 'TopbarStyled.menuLogout' })}
                  </Button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <NamedLink
                      name="LoginPage"
                      className="no-underline hover:no-underline"
                      onClick={handleMobileLinkClick}
                    >
                      <Button variant="outline" className="w-full">
                        {intl.formatMessage({ id: 'TopbarStyled.signIn' })}
                      </Button>
                    </NamedLink>
                    <NamedLink
                      name="SignupPage"
                      className="no-underline hover:no-underline"
                      onClick={handleMobileLinkClick}
                    >
                      <span className="servibe-btn servibe-btn-primary flex items-center justify-center gap-2 px-4 py-2.5 text-sm w-full">
                        <span>{intl.formatMessage({ id: 'TopbarStyled.signUp' })}</span>
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </NamedLink>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default TopbarDesktopStyled;
